import { Col, Row, Button, Label } from "reactstrap";
import { textTranslate } from "../../utils/i18n";
import React, { useState, useEffect, useRef } from "react";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import { DateRangePicker } from "react-dates";
import { useDispatch, useSelector } from "react-redux";
import { connect } from "react-redux";
import axios from "axios";
import moment from "moment";
import "react-dates/initialize";
import API_ENDPOINTS from "../../configs/API_ENDPOINTS";
import "react-dates/lib/css/_datepicker.css";
import "../../views/Components/SelectDateComp.css";

import {
  getDeviceNameList,
  getOrganisationList,
  getDeviceModelList,
  getPackages,
  getErrorCodes,
  getStatusCodes,
} from "../../redux/actions/monitoringAction";

const MONITORING_FILTER_SESSION_KEY = "monitoringFilterCache";

const URL_PARAM_BY_PAYLOAD_KEY = {
  orgIds: "orgs",
  devices: "devices",
  deviceTypeIds: "types",
  packageIds: "packages",
  errorCodes: "errorCodes",
  statusIds: "status",
  errorStartDate: "errorFrom",
  errorEndDate: "errorTo",
  lastSeenStartDate: "lastSeenFrom",
  lastSeenEndDate: "lastSeenTo",
};

const hasFilterParams = (search) => {
  const params = new URLSearchParams(search);
  return Object.values(URL_PARAM_BY_PAYLOAD_KEY).some((param) => params.has(param));
};

const parseFilterSearch = (search) => {
  const params = new URLSearchParams(search);
  const ids = (param) => (params.get(param) || "").split(",").filter(Boolean);
  return {
    orgs: ids("orgs"),
    devices: ids("devices"),
    types: ids("types"),
    packages: ids("packages"),
    errorCodes: ids("errorCodes"),
    status: ids("status"),
    errorFrom: params.get("errorFrom") || "",
    errorTo: params.get("errorTo") || "",
    lastSeenFrom: params.get("lastSeenFrom") || "",
    lastSeenTo: params.get("lastSeenTo") || "",
  };
};

const buildFilterSearch = (queryPayload) => {
  const params = new URLSearchParams();
  Object.entries(URL_PARAM_BY_PAYLOAD_KEY).forEach(([payloadKey, param]) => {
    if (queryPayload[payloadKey]) params.set(param, queryPayload[payloadKey]);
  });
  return params.toString();
};

const readSavedFilterSearch = (locationSearch) => {
  if (hasFilterParams(locationSearch)) return locationSearch;
  try {
    return sessionStorage.getItem(MONITORING_FILTER_SESSION_KEY);
  } catch {
    return null;
  }
};

const selectOptionsByIds = (options, ids) => options.filter((option) => ids.includes(String(option.id)));

const MonitorFilter = (props) => {
  const dispatch = useDispatch();
  const { history, location } = props;
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const { data, language } = props;
  const [originalData, setOriginalData] = useState(data); // store original data
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [selectedDeviceName, setSelectedDeviceType] = useState([]);
  const [selectedDeviceModel, setSelectedDeviceModel] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [selectedErrorCodes, setSelectedErrorCodes] = useState([]);
  const [selectedStatusCodes, setSelectedStatusCodes] = useState([]);
  const [selectedErrorStartDate, setSelectedErrorStartDate] = useState(null);
  const [selectedErrorEndDate, setSelectedErrorEndDate] = useState(null);
  const [selectedLastSeenStartDate, setSelectedLastSeenStartDate] = useState(null);
  const [selectedLastSeenEndDate, setSelectedLastSeenEndDate] = useState(null);
  const [initPhase, setInitPhase] = useState(0);
  const savedFilters = useRef(null);
  const [lastSeenFocusedInput, setLastSeenFocusedInput] = useState(null);
  const [errorDateFocusedInput, setErrorDateFocusedInput] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const organisationList = useSelector((state) => state.monitoringsReducer.organisationList);
  const deviceNameList = useSelector((state) => state.monitoringsReducer.deviceNameList);
  const packages = useSelector(
    (state) => state.monitoringsReducer.packages.map((p) => ({ value: p._id, label: p.name, id: p._id })) || []
  );
  const errorCodes = useSelector(
    (state) => state.monitoringsReducer.errorCodes.map((e) => ({ value: e._id, label: e.name, id: e._id })) || []
  );
  const statusCodes = useSelector(
    (state) => state.monitoringsReducer.statusCodes.map((s) => ({ value: s._id, label: s.name, id: s._id })) || []
  );

  useEffect(() => {
    dispatch(getDeviceNameList());
    dispatch(getOrganisationList());
    dispatch(getDeviceModelList());
    dispatch(getPackages());
    dispatch(getErrorCodes());
    dispatch(getStatusCodes());
    function fetchDevices() {
      axios
        .get(`${API_ENDPOINTS.DEVICES}?perPage=10000`)
        .then((response) => {
          setDevices(
            response.data.data.map((device) => ({
              value: device._id,
              label: `${device.device}-${device.name}-${device.serialNumber}`,
              id: device._id,
            }))
          );
        })
        .catch((error) => {
          console.error("Error fetching devices:", error);
        });
    }
    fetchDevices();
  }, []); // maybe add dispatch here if it doesn't show the list

  useEffect(() => {
    const savedSearch = readSavedFilterSearch(location.search);
    if (savedSearch === null) return;
    savedFilters.current = parseFilterSearch(savedSearch);
    if (savedSearch !== location.search) history.replace({ search: savedSearch });
  }, []); // mount only

  useEffect(() => {
    // Wait for the initial unfiltered data so it can't overwrite the filtered result
    if (initPhase !== 0 || !originalData || originalData.length === 0) return;
    const saved = savedFilters.current;
    if (!saved) {
      applyDefaultFilters();
      setInitPhase(1);
      return;
    }
    const isReady = (ids, options) => ids.length === 0 || options.length > 0;
    const listsReady =
      isReady(saved.orgs, organisationList) &&
      isReady(saved.devices, devices) &&
      isReady(saved.types, deviceNameList) &&
      isReady(saved.packages, packages) &&
      isReady(saved.errorCodes, errorCodes) &&
      isReady(saved.status, statusCodes);
    if (!listsReady) return;
    restoreSavedFilters(saved);
    setInitPhase(1);
  }, [
    initPhase,
    originalData,
    organisationList,
    devices,
    deviceNameList,
    packages.length,
    errorCodes.length,
    statusCodes.length,
  ]);

  const applyDefaultFilters = () => {
    const queryParams = {};
    const defaultPackages = packages.filter((item) =>
      ["premium – automatic replacement", "self service"].includes(item.label.toLowerCase())
    );
    if (defaultPackages) {
      setSelectedPackages(defaultPackages);
      queryParams.service = defaultPackages;
    }
    const defaultErrorCodes = errorCodes.filter((item) => ["heartbeat"].includes(item.label.toLowerCase()));
    if (defaultErrorCodes) {
      setSelectedErrorCodes(defaultErrorCodes);
      queryParams.errorCodes = defaultErrorCodes;
    }
    const defaultStatusCodes = statusCodes.filter((item) => ["active"].includes(item.label.toLowerCase()));
    if (defaultStatusCodes) {
      setSelectedStatusCodes(defaultStatusCodes);
      queryParams.statusCodes = defaultStatusCodes;
    }
    filterDataBasedOnSelection(queryParams);
  };

  const restoreSavedFilters = (saved) => {
    const selections = {
      orgs: selectOptionsByIds(organisationList, saved.orgs),
      devices: selectOptionsByIds(devices, saved.devices),
      deviceNames: selectOptionsByIds(deviceNameList, saved.types),
      service: selectOptionsByIds(packages, saved.packages),
      errorCodes: selectOptionsByIds(errorCodes, saved.errorCodes),
      statusCodes: selectOptionsByIds(statusCodes, saved.status),
      errorStartDate: saved.errorFrom,
      errorEndDate: saved.errorTo,
      lastSeenStartDate: saved.lastSeenFrom,
      lastSeenEndDate: saved.lastSeenTo,
    };
    setSelectedOrganizations(selections.orgs);
    setSelectedDevices(selections.devices);
    setSelectedDeviceType(selections.deviceNames);
    setSelectedPackages(selections.service);
    setSelectedErrorCodes(selections.errorCodes);
    setSelectedStatusCodes(selections.statusCodes);
    setSelectedErrorStartDate(saved.errorFrom || null);
    setSelectedErrorEndDate(saved.errorTo || null);
    setSelectedLastSeenStartDate(saved.lastSeenFrom || null);
    setSelectedLastSeenEndDate(saved.lastSeenTo || null);
    filterDataBasedOnSelection(selections);
  };

  const persistFilterSearch = (search) => {
    history.replace({ search });
    try {
      sessionStorage.setItem(MONITORING_FILTER_SESSION_KEY, search);
    } catch {
      // sessionStorage unavailable; the URL still holds the filters
    }
  };

  useEffect(() => {
    setOriginalData(data);
  }, [data]);

  const onChangeOrganisation = (selectedOptions) => {
    setSelectedOrganizations(selectedOptions);
    filterDataBasedOnSelection({ orgs: selectedOptions });
  };

  const onChangeDeviceName = (selectedOptions) => {
    setSelectedDeviceType(selectedOptions);
    filterDataBasedOnSelection({ deviceNames: selectedOptions });
  };

  const onChangePackage = (selectedOptions) => {
    setSelectedPackages(selectedOptions);
    filterDataBasedOnSelection({ service: selectedOptions });
  };

  const onChangeErrorCodes = (selectedOptions) => {
    setSelectedErrorCodes(selectedOptions);
    filterDataBasedOnSelection({
      errorCodes: selectedOptions,
    });
  };

  const handleLastSeenChange = (dateRanges) => {
    const { startDate, endDate } = dateRanges;
    const fromDate = startDate ? moment(startDate).toISOString() : null;
    const toDate = endDate ? moment(endDate).toISOString() : null;
    setSelectedLastSeenStartDate(fromDate);
    setSelectedLastSeenEndDate(toDate);
    if (fromDate || toDate) {
      filterDataBasedOnSelection({
        lastSeenStartDate: fromDate,
        lastSeenEndDate: toDate,
      });
    } else {
      filterDataBasedOnSelection({
        lastSeenStartDate: "",
        lastSeenEndDate: "",
      });
    }
  };

  const handleErrorDateChange = (dateRanges) => {
    const { startDate, endDate } = dateRanges;
    if (!startDate) {
      setSelectedErrorStartDate(null);
    }
    if (!endDate) {
      setSelectedErrorEndDate(null);
    }
    const fromDate = startDate ? moment(startDate).toISOString() : null;
    const toDate = endDate ? moment(endDate).toISOString() : null;
    setSelectedErrorStartDate(startDate);
    setSelectedErrorEndDate(endDate);
    if (fromDate || toDate) {
      filterDataBasedOnSelection({
        errorStartDate: fromDate ?? "",
        errorEndDate: toDate ?? "",
      });
    } else {
      filterDataBasedOnSelection({
        errorStartDate: "",
        errorEndDate: "",
      });
    }
  };

  const onChangeStatusCode = (selectedOptions) => {
    setSelectedStatusCodes(selectedOptions);
    const queryParams = {
      statusCodes: selectedOptions,
    };

    filterDataBasedOnSelection(queryParams);
  };

  const onChangeDevices = (selectedOptions) => {
    setSelectedDevices(selectedOptions);

    const queryParams = {
      devices: selectedOptions,
    };

    filterDataBasedOnSelection(queryParams);
  };

  const filterDataBasedOnSelection = (queryParams) => {
    const {
      orgs,
      devices,
      deviceNames,
      deviceModels,
      service,
      errorCodes,
      errorStartDate,
      errorEndDate,
      lastSeenStartDate,
      lastSeenEndDate,
      statusCodes,
    } = queryParams;
    const queryPayload = {
      devices: devices ? devices.map((item) => item.id).join(",") : selectedDevices.map((item) => item.id).join(","),
      orgIds: orgs ? orgs.map((item) => item.id).join(",") : selectedOrganizations.map((item) => item.id).join(","),
      deviceTypeIds: deviceNames
        ? deviceNames.map((item) => item.id).join(",")
        : selectedDeviceName.map((item) => item.id).join(","),
      deviceModelIds: deviceModels
        ? deviceModels.map((item) => item.id).join(",")
        : selectedDeviceModel.map((item) => item.id).join(","),
      packageIds: service ? service.map((item) => item.id).join(",") : selectedPackages.map((item) => item.id).join(","),
      errorCodes: errorCodes
        ? errorCodes.map((item) => item.value).join(",")
        : selectedErrorCodes.map((item) => item.value).join(","),
      errorStartDate: errorStartDate ?? moment(selectedErrorStartDate).toISOString(),
      errorEndDate: errorEndDate ?? moment(selectedErrorEndDate).toISOString(),
      lastSeenStartDate: lastSeenStartDate ?? moment(selectedLastSeenStartDate).toISOString(),
      lastSeenEndDate: lastSeenEndDate ?? moment(selectedLastSeenEndDate).toISOString(),
      statusIds: statusCodes
        ? statusCodes.map((item) => item.value).join(",")
        : selectedStatusCodes.map((item) => item.value).join(","),
    };
    persistFilterSearch(buildFilterSearch(queryPayload));
    props.onFilteredDevices(queryPayload);
  };

  useEffect(() => {
    moment.locale(language);
  }, [props.language]);

  const clearFilter = () => {
    setSelectedErrorCodes([]);
    setSelectedPackages([]);
    setSelectedDeviceModel([]);
    setSelectedDeviceType([]);
    setSelectedOrganizations([]);
    setSelectedErrorStartDate(null);
    setSelectedErrorEndDate(null);
    setSelectedStatusCodes([]);
    setSelectedLastSeenStartDate(null);
    setSelectedLastSeenEndDate(null);
    setSelectedDevices([]);
    persistFilterSearch("");
    props.onFilteredDevices(null);
  };

  return (
    <React.Fragment>
      {isFilterOpen ? (
        <React.Fragment>
          <Row>
            <Col sm="12" md="6" className="d-flex justify-content-start">
              <Button color="link" onClick={() => setIsFilterOpen(!isFilterOpen)}>
                <i className="fa fa-minus" /> {textTranslate("hideAdvancedFiltering")}
              </Button>
            </Col>
            <Col sm="12" md="6" className="d-flex justify-content-end">
              <Button color="info" onClick={() => clearFilter()}>
                <i className="fa fa-eraser" /> {textTranslate("clearFilter")}
              </Button>
            </Col>
          </Row>
          <Row>
            {/* Organizations */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("ownersLabel")}</Label>
              <Select
                options={organisationList}
                value={selectedOrganizations}
                onChange={onChangeOrganisation}
                className="w-100"
                isMulti={true}
                autoFocus
              ></Select>
            </Col>
            {/* Devices */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("devices")}</Label>
              <Select
                options={devices}
                value={selectedDevices}
                onChange={onChangeDevices}
                className="w-100"
                isMulti={true}
                autoFocus
              ></Select>
            </Col>
            {/* Device types */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("dashboard.deviceType")}</Label>
              <Select
                options={deviceNameList}
                value={selectedDeviceName}
                onChange={onChangeDeviceName}
                className="w-100"
                isMulti={true}
              ></Select>
            </Col>
            {/* Packages */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("serviceLevel")}</Label>
              <Select
                options={packages}
                value={selectedPackages}
                onChange={onChangePackage}
                className="w-100"
                isMulti={true}
                valueKey="_id"
                labelKey="name"
              ></Select>
            </Col>
          </Row>
          <Row>
            {/* Error codes */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">{textTranslate("errorCode")}</Label>
              <Select
                options={errorCodes}
                value={selectedErrorCodes}
                onChange={onChangeErrorCodes}
                className="w-100"
                isMulti={true}
              ></Select>
            </Col>
            {/* Status */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted mr-2">Status</Label>
              <Select
                options={statusCodes}
                value={selectedStatusCodes}
                onChange={onChangeStatusCode}
                className="w-100"
                isMulti={true}
              ></Select>
            </Col>
            {/* ErrorDate */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted">{textTranslate("errorDate")}</Label>
              <DateRangePicker
                required={false}
                startDate={selectedErrorStartDate ? moment(selectedErrorStartDate) : null}
                startDateId="startErrorDateId"
                endDate={selectedErrorEndDate ? moment(selectedErrorEndDate) : null}
                endDateId="endErrorDateId"
                onDatesChange={({ startDate, endDate }) => handleErrorDateChange({ startDate, endDate })}
                focusedInput={errorDateFocusedInput}
                onFocusChange={(focusedInput) => setErrorDateFocusedInput(focusedInput)}
                enableOutsideDays={false}
                anchorDirection="right"
                openDirection={"down"}
                numberOfMonths={1}
                small
                showDefaultInputIcon
                block
                isOutsideRange={(day) => moment(day).isAfter(moment().add(1, "day"))}
                showClearDates={true}
                renderDayContents={(day) => {
                  day.local(language);
                  return moment(day).format("D");
                }}
              />
            </Col>
            {/* LastSeen */}
            <Col sm="12" md="3" className="mt-1 d-flex flex-column">
              <Label className="text-muted">{textTranslate("lastSeenAt")}</Label>
              <DateRangePicker
                required={false}
                startDate={selectedLastSeenStartDate ? moment(selectedLastSeenStartDate) : null}
                startDateId="startDateId"
                endDate={selectedLastSeenEndDate ? moment(selectedLastSeenEndDate) : null}
                endDateId="endDateId"
                onDatesChange={({ startDate, endDate }) => handleLastSeenChange({ startDate, endDate })}
                focusedInput={lastSeenFocusedInput}
                onFocusChange={(focusedInput) => setLastSeenFocusedInput(focusedInput)}
                enableOutsideDays={false}
                anchorDirection="right"
                openDirection={"down"}
                numberOfMonths={1}
                small
                showDefaultInputIcon
                block
                isOutsideRange={(day) => moment(day).isAfter(moment().add(1, "day"))}
                showClearDates={true}
                renderDayContents={(day) => {
                  day.local(language);
                  return moment(day).format("D");
                }}
              />
            </Col>
          </Row>
        </React.Fragment>
      ) : (
        <Row>
          <Col sm="12" md="6" className="clear-fix">
            <Button
              color="link"
              className="pull-left"
              onClick={() => {
                setIsFilterOpen(true);
              }}
            >
              <i className="fa fa-plus mr-1" /> {textTranslate("showAdvancedFiltering")}
            </Button>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = ({ i18nReducer }) => ({
  language: i18nReducer.language,
});

export default connect(mapStateToProps, null)(withRouter(MonitorFilter));
